import React from 'react';
import * as Sentry from '@sentry/browser';
import DocumentNodeForm from 'routes/surface/components/node-modal-form/components/document-node-modal/components/document-node-form';
import UploadFileForm from 'routes/surface/components/node-modal-form/components/document-node-modal/components/document-node-form/components/upload-file-form';
import ImageNodeForm from 'routes/surface/components/node-modal-form/components/image-node-modal/components/image-node-form';
import If from 'components/if-statement';
import useSaveSurfaceDocumentNode from 'routes/surface/components/node-modal-form/hooks/use-save-surface-document-node';
import useSaveSurfaceImageNode from 'routes/surface/components/node-modal-form/hooks/use-save-surface-image-node';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useFileToLocalUrl from 'hooks/use-file-to-local-url';
import { useUploadSurfaceNodeFile } from 'components/canvas/hooks/use-upload-surface-node-file';
import { useTranslation } from 'i18n';
import { withModal } from 'hoc';
import { asyncAction } from 'utils/js';
import { set } from 'lodash/fp';
import { toast } from 'services/toast';
import { ModalContext } from 'hoc/with-modal';
import {
  SurfaceDocumentNode,
  SurfaceDocumentNodeFormat,
  SurfaceImageNodeFormat,
  SurfaceNodeType,
} from 'graphql/types';
import { NodeModalInput } from 'types';

export interface DocumentNodeModalContentProps {
  data: NodeModalInput<SurfaceDocumentNode>;
  modal: ModalContext;
  onFileChange?: Function;
}

function DocumentNodeModalContent(props: DocumentNodeModalContentProps) {
  const { data } = props;
  const { t } = useTranslation();

  // @ts-ignore
  const surfaceId = data.surface?.id ?? data.surface;

  const [shouldShowFilePreview, setShowFilePreview] = React.useState(
    Boolean(data.id),
  );

  const [file, setFile] = React.useState<File | undefined>();
  const [name, setName] = React.useState<string>('');
  const fileUrl = useFileToLocalUrl(file);

  const saveSurfaceDocumentNode = useSaveSurfaceDocumentNode(surfaceId);
  const saveSurfaceImageNode = useSaveSurfaceImageNode(surfaceId);
  const uploadFile = useUploadSurfaceNodeFile(surfaceId);
  const saveNodeInCache = useSaveNodeInCache();

  async function onSubmit(input, { setSubmitting }) {
    const isDocument =
      input?.file?.type.includes('pdf') ||
      input?.type === SurfaceNodeType.Document;

    const saveOperation = isDocument
      ? saveSurfaceDocumentNode
      : saveSurfaceImageNode;

    const response = await saveOperation({ variables: { input } });

    if (response.isFailure) {
      setSubmitting(false);
      toast.error(response.getErrorMessage());

      return response;
    }

    const node = response.getField('data');

    // Upload a file  then set correct values and submit data
    const [error] = await asyncAction(uploadFile(input.file, node.id));

    if (error) {
      Sentry.captureException(error);
    }

    saveNodeInCache(set('updatedAt', new Date().toISOString(), node));

    return response;
  }

  function onFileChange(file) {
    setFile(file);
    props.onFileChange && props.onFileChange(file);
  }

  function onNameChange(name) {
    setName(name);
  }

  function showFilePreview() {
    setShowFilePreview(true);
  }

  function getMimeType(file): string | undefined {
    return file?.type
  }

  function getFileFormat(file): string | undefined {
    if (file?.type.includes('pdf')) {
      return SurfaceDocumentNodeFormat.Pdf;
    }

    if (file?.type.includes('jpg')) {
      return SurfaceImageNodeFormat.Jpg;
    }

    if (file?.type.includes('jpeg')) {
      return SurfaceImageNodeFormat.Jpeg;
    }

    if (file?.type.includes('png')) {
      return SurfaceImageNodeFormat.Png;
    }
   
    return SurfaceDocumentNodeFormat.File;
  
  }

  function getNodeTypeForFile(file): string | undefined {
    if (file) {
      if (file?.type.includes('pdf')) {
        return SurfaceNodeType.Document;
      }
      if (file?.type.includes('video')) {
        return SurfaceNodeType.Document;
      }
      return SurfaceNodeType.Image;
    }

    return undefined;
  }

  function backButtonRenderer(BackButton) {
    if (data.id) {
      return null;
    }

    return (
      <BackButton onClick={() => setShowFilePreview(false)}>
        {t('Back to file upload')}
      </BackButton>
    );
  }

  const type = getNodeTypeForFile(file) ?? data?.type;

  const initialValues = {
    ...data,
    file,
    name: name || data.name,
    format: getFileFormat(file),
    mimeType : getMimeType(file),
    ...(file ? { type, url: fileUrl } : {}),
  } as NodeModalInput<SurfaceDocumentNode>;

  return (
    <If condition={shouldShowFilePreview}>
      <If.Then>
        <If condition={type === SurfaceNodeType.Document}>
          <If.Then>
            <DocumentNodeForm
              initialValues={initialValues}
              onSubmit={onSubmit}
              closeForm={props.modal.hide}
              backButton={backButtonRenderer}
            />
          </If.Then>
          <If.Else>
            <ImageNodeForm
              initialValues={initialValues}
              onSubmit={onSubmit}
              onCancel={props.modal.hide}
              backButton={backButtonRenderer}
            />
          </If.Else>
        </If>
      </If.Then>
      <If.Else>
        <UploadFileForm
          onNameChange={onNameChange}
          onFileChange={onFileChange}
          onCancel={props.modal.hide}
          onSubmit={showFilePreview}
        />
      </If.Else>
    </If>
  );
}

export default withModal(DocumentNodeModalContent);
